import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Particle } from "./types";
import { createSparkAt, updateCanvasFilter } from "./sparkHelpers";
import { useSparkContext } from "./SparkContext";

const SparkEffect: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const hoverTimestampsRef = useRef<Map<Element, number>>(new Map());
  const animationFrameRef = useRef<number | null>(null);
  const { previewParams } = useSparkContext();

  // Memoize the resize function to prevent unnecessary recreations
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
  }, []);

  // Memoize the animation function to prevent unnecessary recreations
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const now = Date.now();
    const elements = document.querySelectorAll('[data-spark="true"]');
    const hoverTimestamps = hoverTimestampsRef.current;

    updateCanvasFilter(canvas, hoverTimestamps, elements);

    elements.forEach(el => {
      const hovered = el.matches(':hover');
      const baseRate = hovered ? 0.3 : 0.05;
      const t = (now % 4000) / 4000;
      const easedRate = baseRate + 0.2 * (1 - Math.cos(t * 2 * Math.PI)) / 2;
      if (Math.random() < easedRate) {
        // Pass the preview params to createSparkAt for real-time updates
        createSparkAt(el, particlesRef, hoverTimestamps, previewParams);
      }
    });

    particlesRef.current = particlesRef.current.filter(p => p.life > 0);
    particlesRef.current.forEach(p => {
      p.vx += (Math.random() - 0.5) * 0.002;
      p.vy += (Math.random() - 0.5) * 0.002;
      p.x += p.vx;
      p.y += p.vy;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(${p.color},${Math.min(1, p.life / 120)})`;
      ctx.fill();
      p.life--;
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [previewParams]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial resize
    resize();

    // Set up event listeners
    window.addEventListener('resize', resize);
    
    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(animate);

    // Clean up function
    return () => {
      window.removeEventListener('resize', resize);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [resize, animate]);

  // Memoize the canvas element to prevent unnecessary rerenders
  const canvasElement = useMemo(() => (
    <canvas 
      id="sparks" 
      ref={canvasRef} 
      className="spark-canvas"
    />
  ), []);

  return canvasElement;
};

export default React.memo(SparkEffect);
